import { useEffect, useState } from "react";
import { Phone, Mail, MessageCircle, Send, MapPin } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const REASONS = [
  { value: "donate", label: "I want to donate" },
  { value: "volunteer", label: "I want to volunteer" },
  { value: "partner", label: "I want to partner" },
  { value: "support", label: "I need help/support" },
  { value: "media", label: "Media enquiry" },
  { value: "general", label: "General enquiry" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", reason: "general", message: "" });
  const [loading, setLoading] = useState(false);
  useEffect(() => { document.title = "Contact | Dutsen Kauna Foundation"; }, []);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", reason: "general", message: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative bg-[#155e3f] text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] to-[#155e3f]" />
        <div className="dk-container relative">
          <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">Contact</span>
          <h1 className="font-serif text-4xl md:text-6xl font-bold mt-3 max-w-3xl">Talk to us. We're listening.</h1>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="dk-container grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-6">
            <div className="p-6 rounded-2xl bg-[#155e3f] text-white" data-testid="contact-head-office">
              <div className="flex items-center gap-3 mb-2"><MapPin className="text-[#d6aa43]" size={18} /><span className="text-xs uppercase tracking-[0.2em] text-white font-semibold">Head Office</span></div>
              <div className="font-serif text-2xl font-bold">Abuja, Nigeria</div>
              <div className="text-sm text-white mt-1">Dutsen Kauna Foundation, Dutsen Kauna, Abuja-FCT, Nigeria</div>
            </div>
            <div className="p-6 rounded-2xl bg-[#f8f4e8] border border-[#d6aa43]/20">
              <div className="flex items-center gap-3 mb-2"><Phone className="text-[#d6aa43]" size={18} /><span className="text-xs uppercase tracking-[0.2em] text-[#0d1724]/60 font-semibold">Phone / WhatsApp</span></div>
              <a href="tel:+2348063550963" className="font-serif text-2xl text-[#0a0d12] font-bold hover:text-[#d6aa43]">+234 806 355 0963</a>
            </div>
            <div className="p-6 rounded-2xl bg-[#f8f4e8] border border-[#d6aa43]/20">
              <div className="flex items-center gap-3 mb-2"><Mail className="text-[#d6aa43]" size={18} /><span className="text-xs uppercase tracking-[0.2em] text-[#0d1724]/60 font-semibold">Email</span></div>
              <a href="mailto:foundation@dutsenkauna.org" className="block text-[#0a0d12] font-semibold hover:text-[#d6aa43] break-all">foundation@dutsenkauna.org</a>
              <a href="mailto:foundation@dutsenkauna.org" className="block text-[#0a0d12]/80 hover:text-[#d6aa43] break-all">foundation@dutsenkauna.org</a>
            </div>
            <a href="https://wa.me/2348063550963" target="_blank" rel="noopener noreferrer" className="block p-6 rounded-2xl bg-[#25D366] text-white hover:opacity-90 transition" data-testid="whatsapp-cta">
              <div className="flex items-center gap-3"><MessageCircle size={22} /><span className="font-serif text-xl font-bold">Chat on WhatsApp</span></div>
              <div className="text-sm mt-1 opacity-90">Fastest way to reach us — typically within hours.</div>
            </a>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 p-8 md:p-10 rounded-2xl bg-[#155e3f] text-white" data-testid="contact-form">
            <h2 className="font-serif text-2xl md:text-3xl font-bold mb-6">Send us a message</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name" required value={form.name} onChange={(v) => update("name", v)} testid="contact-name" />
              <Field label="Email" type="email" required value={form.email} onChange={(v) => update("email", v)} testid="contact-email" />
              <Field label="Phone" value={form.phone} onChange={(v) => update("phone", v)} testid="contact-phone" />
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-white font-semibold mb-2 block">Reason for Contact</label>
                <select
                  value={form.reason}
                  onChange={(e) => update("reason", e.target.value)}
                  className="w-full bg-[#155e3f] border border-white/15 rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#d6aa43]"
                  data-testid="contact-reason"
                >
                  {REASONS.map((r) => (<option key={r.value} value={r.value}>{r.label}</option>))}
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs uppercase tracking-[0.18em] text-white font-semibold mb-2 block">Message *</label>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="w-full bg-[#155e3f] border border-white/15 rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#d6aa43]"
                data-testid="contact-message"
              />
            </div>
            <button type="submit" disabled={loading} className="dk-btn-primary mt-6 disabled:opacity-60" data-testid="contact-submit">
              {loading ? "Sending..." : "Send message"} <Send size={16} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-white font-semibold mb-2 block">{label}{required ? " *" : ""}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#155e3f] border border-white/15 rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#d6aa43]"
        data-testid={testid}
      />
    </div>
  );
}
